import Feature from 'ol/Feature'
import Point from 'ol/geom/Point'
import VectorLayer from 'ol/layer/Vector'
import { fromLonLat } from 'ol/proj'
import Cluster from 'ol/source/Cluster'
import VectorSource from 'ol/source/Vector'
import { Circle as CircleStyle, Fill, Stroke, Style, Text } from 'ol/style'

import { SITE_STATUS_META } from '../lib/lens'

import type { GisSite } from '../api/types'
import type { SiteIssueCount } from '../lib/issues'
import type { StyleFunction } from 'ol/style/Style'

export const SITE_LAYER_ID = 'gis-sites'
export const CLUSTER_DISTANCE = 42

export function buildSiteLayer(sites: GisSite[]): VectorLayer<Cluster<Feature<Point>>> {
  const source = new VectorSource({
    // Un-geocoded sites (null lon/lat) get no marker — they stay searchable but off the map.
    features: sites
      .filter((s): s is GisSite & { lon: number; lat: number } => s.lon !== null && s.lat !== null)
      .map((s) => {
        const f = new Feature({ geometry: new Point(fromLonLat([s.lon, s.lat])) })
        f.set('site', s)
        return f
      }),
  })
  const layer = new VectorLayer({
    source: new Cluster({ distance: CLUSTER_DISTANCE, source }),
    zIndex: 10,
  })
  layer.set('id', SITE_LAYER_ID)
  return layer
}

export interface IssueMarker {
  /** Worst-severity colour: red if any active issue is critical, else amber. */
  color: string
  /** The open-issue count, as marker text. */
  label: string
}

/**
 * Severity-count marker for a site on the Issues map: the open-issue count + the worst-severity colour
 * (red if any active issue is critical, else amber). Null when the site has no open issues.
 */
export function issueMarker(count: SiteIssueCount | undefined): IssueMarker | null {
  if (!count || count.open <= 0) {
    return null
  }
  return { color: count.critical > 0 ? '#dc2626' : '#f59e0b', label: String(count.open) }
}

/** Cluster bubble. With the issue overlay on, a red stroke flags a cluster that hides ≥1 critical site. */
function clusterStyle(members: Feature[], overlay?: Map<string, SiteIssueCount>): Style {
  const anyCritical = overlay
    ? members.some((f) => (overlay.get((f.get('site') as GisSite).id)?.critical ?? 0) > 0)
    : false
  return new Style({
    image: new CircleStyle({
      radius: 13,
      fill: new Fill({ color: '#1e293b' }),
      stroke: new Stroke({ color: anyCritical ? '#dc2626' : '#fff', width: anyCritical ? 3 : 2 }),
    }),
    text: new Text({ text: String(members.length), fill: new Fill({ color: '#fff' }), font: '600 11px sans-serif' }),
  })
}

/**
 * Single-site marker, enlarged + yellow-ringed when selected OR hovered. On the Issues map (overlay
 * active and the site has open issues) it renders as a **severity-count marker** — a filled
 * severity-coloured circle showing the open-issue count; elsewhere it's the plain status dot.
 */
function siteStyle(s: GisSite, emphasized: boolean, overlay?: Map<string, SiteIssueCount>): Style {
  const stroke = new Stroke({ color: emphasized ? '#fde047' : '#fff', width: emphasized ? 3 : 1.5 })
  const marker = overlay ? issueMarker(overlay.get(s.id)) : null
  if (marker) {
    return new Style({
      image: new CircleStyle({ radius: emphasized ? 12 : 10, fill: new Fill({ color: marker.color }), stroke }),
      text: new Text({ text: marker.label, fill: new Fill({ color: '#fff' }), font: '700 10px sans-serif' }),
    })
  }
  return new Style({
    image: new CircleStyle({ radius: emphasized ? 9 : 6, fill: new Fill({ color: SITE_STATUS_META[s.status].color }), stroke }),
  })
}

/**
 * Style factory: rebuild + setStyle() when the selected, hovered, or focused site, or the issue overlay
 * changes. A single marker is emphasized (enlarged + yellow stroke) when it is the selected OR hovered OR
 * focused site (the last driven by clicking an issue in the board). `overlay` (siteId → counts) is omitted
 * when no ring overlay is active, in which case markers render plainly.
 */
export function siteStyleFn(
  selectedSiteId: string | null,
  hoveredSiteId: string | null,
  overlay?: Map<string, SiteIssueCount>,
  focusedSiteId?: string | null,
): StyleFunction {
  return (feature) => {
    const members = feature.get('features') as Feature[]
    if (members.length > 1) {
      return clusterStyle(members, overlay)
    }
    const s = members[0]!.get('site') as GisSite
    return siteStyle(s, selectedSiteId === s.id || hoveredSiteId === s.id || focusedSiteId === s.id, overlay)
  }
}
